import React, { useEffect, useState } from "react";
import { Globe2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Public "Now covering N places across N cities" chip.
 *
 *   variant="pill"  → compact emerald pill (used in the /places header)
 *   variant="tile"  → full-width home-page tile with subtle live-pulse dot
 *
 * Hides silently until `total_places > 0` so pre-densify visitors don't
 * see a zero. Cache-friendly — the underlying endpoint is public and
 * O(cities-tracked) count_documents, easy to CDN in front.
 */
export default function CoverageChip({ variant = "pill", className = "" }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/places/coverage")
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  if (!data || !data.total_places) return null;
  const places = data.total_places.toLocaleString();
  const cities = data.cities_covered || 0;

  if (variant === "tile") {
    return (
      <div
        className={`rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-4 shadow-sm ${className}`}
        data-testid="coverage-tile"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-emerald-100">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-200" />
          </span>
          Live network
          {data.added_24h > 0 && (
            <span
              className="ml-auto inline-flex items-center rounded-full bg-emerald-500/40 border border-emerald-300/40 px-2 py-0.5 text-[10px] font-bold tabular-nums"
              data-testid="coverage-tile-ticker"
              title={`${data.added_24h} places added in the last 24 hours`}
            >
              +{data.added_24h.toLocaleString()} today
            </span>
          )}
        </div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="text-2xl font-black tabular-nums" data-testid="coverage-tile-places">{places}</span>
          <span className="text-sm font-bold text-emerald-100">places</span>
          {cities > 0 && (
            <>
              <span className="text-xs text-emerald-200">·</span>
              <span className="text-2xl font-black tabular-nums" data-testid="coverage-tile-cities">{cities}</span>
              <span className="text-sm font-bold text-emerald-100">cities</span>
            </>
          )}
        </div>
        <div className="text-[11px] text-emerald-100 mt-1">Growing daily · deduped worldwide.</div>
      </div>
    );
  }

  // Default compact pill
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 ${className}`}
      data-testid="places-coverage-banner"
      title={`Tracking ${data.cities_tracked} cities across ${data.countries?.length || 0} countries`}
    >
      <Globe2 size={12} />
      Now covering <span className="tabular-nums">{places}</span> places
      {cities > 0 && <> across <span className="tabular-nums">{cities}</span> cities</>}
    </div>
  );
}
